/**
 * Personel Kurulum Sayfası.
 *
 * Akış:
 *  1. URL'den token okunur (?token=ABC...)
 *  2. Anonymous Firebase Auth ile giriş
 *  3. Cloud Function 'kurulumTamamla' çağrılır
 *     - Token doğrular
 *     - Cihazı kayıt eder
 *     - Anonim oturuma 'personelId' custom claim ekler
 *  4. ID token refresh edilir
 *  5. /personel'e yönlendirilir
 */

import { useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { signInAnonymously } from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { getFirebaseAuth, getFirebaseFunctions } from '@/lib/firebase'
import { cihazFingerprint, cihazAdi } from '@/lib/cihaz'
import { CheckCircle2, Smartphone, AlertCircle, Loader2 } from 'lucide-react'

type Asama = 'baslangic' | 'kuruluyor' | 'basarili' | 'hata'

export function KurulumPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [asama, setAsama] = useState<Asama>('baslangic')
  const [hata, setHata] = useState<string | null>(null)

  const token = searchParams.get('token')

  async function kurulumBaslat() {
    if (!token) {
      setHata('Token bulunamadı. QR kodu yeniden okut.')
      setAsama('hata')
      return
    }

    setAsama('kuruluyor')
    setHata(null)

    try {
      // 1. Anonymous sign-in
      const auth = getFirebaseAuth()
      await signInAnonymously(auth)

      // 2. Fingerprint + cihaz adı
      const fingerprint = await cihazFingerprint()
      const adi = cihazAdi()

      // 3. Cloud Function çağır
      const functions = getFirebaseFunctions()
      const fn = httpsCallable<
        { token: string; cihazAdi: string; fingerprint: string },
        { success: boolean; personelId: string; cihazId: string }
      >(functions, 'kurulumTamamla')

      const result = await fn({
        token,
        cihazAdi: adi,
        fingerprint,
      })

      if (!result.data.success) {
        throw new Error('Kurulum başarısız')
      }

      // 4. ID token'ı refresh et — yeni custom claim'i al
      await auth.currentUser?.getIdToken(true)

      setAsama('basarili')

      // 5. 2 saniye sonra panele yönlendir + reload
      setTimeout(() => {
        window.location.href = '/personel'
      }, 2000)
    } catch (err) {
      console.error('Kurulum hatası:', err)
      const error = err as { code?: string; message?: string }
      const mesaj = error.message ?? ''

      if (mesaj.includes('not-found') || mesaj.includes('bulunamadı')) {
        setHata('QR geçersiz veya bulunamadı.')
      } else if (mesaj.includes('kullanılmış')) {
        setHata('Bu QR daha önce kullanılmış. Yöneticinden yeni QR iste.')
      } else if (mesaj.includes('süresi dolmuş')) {
        setHata('QR süresi dolmuş. Yöneticinden yeni QR iste.')
      } else if (mesaj.includes('pasif')) {
        setHata('Hesabın pasif durumda. Yöneticine başvur.')
      } else if (error.code === 'unavailable') {
        setHata('Sunucuya ulaşılamıyor. İnternet bağlantını kontrol et.')
      } else {
        setHata('Kurulum başarısız: ' + (mesaj || 'Bilinmeyen hata'))
      }

      setAsama('hata')
    }
  }

  return (
    <div className="page-container bg-gradient-to-br from-brand-50 via-white to-indigo-50 flex items-center justify-center min-h-screen">
      <div className="max-w-md mx-auto px-4 py-8 w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Shift Beat</h1>
          <p className="text-sm text-gray-500 mt-1">Personel Kurulumu</p>
        </div>

        <div className="card animate-slide-up">
          {asama === 'baslangic' && (
            <div className="text-center space-y-4">
              <div className="bg-brand-50 p-4 rounded-2xl inline-block">
                <Smartphone className="w-16 h-16 text-brand-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                Telefonu Sisteme Bağla
              </h2>
              <p className="text-gray-600 text-sm">
                Bu kurulum sonrası bu telefon senin sistemdeki cihazın olacak.
                Cihaz değişikliği için yöneticine başvur.
              </p>
              {!token && (
                <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-xl p-3">
                  ⚠️ QR kodda token yok. Yöneticinden yeni QR iste.
                </div>
              )}
              <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs rounded-xl p-3 text-left">
                ⚠️ Eğer önceden başka bir cihazın varsa, bu kurulum sonrası eski
                cihazın otomatik kapatılacak.
              </div>
              <button
                onClick={kurulumBaslat}
                className="btn-primary w-full"
                disabled={!token}
              >
                Kurulumu Başlat
              </button>
              <button
                onClick={() => navigate('/giris', { replace: true })}
                className="w-full text-sm text-brand-700 hover:text-brand-900 underline py-1"
              >
                Giriş ekranına dön
              </button>
            </div>
          )}

          {asama === 'kuruluyor' && (
            <div className="text-center space-y-4 py-8">
              <Loader2 className="w-16 h-16 text-brand-600 mx-auto animate-spin" />
              <h2 className="text-xl font-bold text-gray-900">Kuruluyor...</h2>
              <p className="text-gray-600 text-sm">
                Cihazın bağlanıyor, lütfen bekle.
              </p>
            </div>
          )}

          {asama === 'basarili' && (
            <div className="text-center space-y-4 py-8">
              <div className="bg-green-50 p-4 rounded-2xl inline-block animate-check-pop">
                <CheckCircle2 className="w-16 h-16 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                Kurulum Tamamlandı! 🎉
              </h2>
              <p className="text-gray-600 text-sm">
                Panele yönlendiriliyorsun...
              </p>
            </div>
          )}

          {asama === 'hata' && (
            <div className="space-y-4">
              <div className="text-center">
                <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-3" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  Kurulum Yapılamadı
                </h2>
              </div>
              {hata && (
                <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-xl p-3">
                  {hata}
                </div>
              )}
              <button
                onClick={() => {
                  setAsama('baslangic')
                  setHata(null)
                }}
                className="btn-secondary w-full"
              >
                Tekrar Dene
              </button>
              <button
                onClick={() => navigate('/giris', { replace: true })}
                className="w-full text-sm text-brand-700 hover:text-brand-900 underline py-1"
              >
                Giriş ekranına dön
              </button>
            </div>
          )}
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-400">Yardım için yöneticine başvur.</p>
        </div>
      </div>
    </div>
  )
}
